import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Card, Button, Spinner, Container, Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import { GetJson, PostJson } from '@awp/helpers/fetchReact';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import EntityTable from './EntityTable';
import {
  setWeatherForecastExternalObjects,
  currentWeatherForecastExternalObjects,
} from '@awp/stores/hazardForecastSlice';

async function listForecasts({ eventId }) {
  const result = await PostJson('/files/list', { eventId })
    .then(async (resp) => {
      if (!resp.ok) {
        throw new Error('Network error');
      }

      return { status: true, data: await resp.json() };
    })
    .catch(() => ({ status: false, data: [] }));

  return result;
}

function EntityAccordion({ eventId, isTransmitted }) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const weatherForecastExternalObjects = useSelector(currentWeatherForecastExternalObjects);
  const [loading, setLoading] = useState(false);

  const hasReadPermission = currentUser.Permissions.includes('READ_AVALANCHE_HAZARD');
  const hasWritePermission = currentUser.Permissions.includes('WRITE_AVALANCHE_HAZARD');

  const fetchData = async (eventId) => {
    if (!hasReadPermission) return;

    setLoading(true);
    const { status, data } = await listForecasts({ eventId });

    if (status) {
      dispatch(setWeatherForecastExternalObjects(data));
    } else {
      dispatch(setWeatherForecastExternalObjects([]));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!eventId) return;
    fetchData(eventId);
  }, [eventId]);

  if (!hasReadPermission) return null;

  if (loading) {
    return (
      <Container>
        <Spinner animation="border" />
      </Container>
    );
  }

  if (weatherForecastExternalObjects.length === 0) {
    return null;
  }

  return (
    <Accordion defaultActiveKey="0">
      <Card border="light" className="page-card">
        <CardHeaderToggle eventKey="0" as="h3" title={'Weather Forecasts'} />
        <Accordion.Collapse eventKey="0">
          <Card.Body>
            <EntityTable
              eventId={eventId}
              isTransmitted={isTransmitted}
              data={weatherForecastExternalObjects}
              onUpdate={() => {
                fetchData(eventId);
              }}
            />
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
}

EntityAccordion.propTypes = {
  eventId: PropTypes.number,
  isTransmitted: PropTypes.bool,
};

export default EntityAccordion;
